import { ArrowRight, Calendar, Clock, Utensils } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AirlineLogo } from '../shared/AirlineLogo';
import { BookingStatusBadge, BookingClassBadge } from '../shared/StatusBadge';
import { formatDate, formatTime, formatDuration } from '../utils/formatters';
import { getStatusColor } from '../utils/statusCodes';
import type { FlightSegment, FlightDetails, TravelDocument, FrequentFlyerCard, Dictionaries } from '../utils/types';

interface FlightSegmentCardProps {
  segment: FlightSegment;
  flightDetails: FlightDetails;
  dictionaries?: Dictionaries;
  relatedTickets?: TravelDocument[];
  relatedFFCards?: FrequentFlyerCard[];
  showConnectionTime?: boolean;
  className?: string;
}

export function FlightSegmentCard({
  segment,
  flightDetails,
  dictionaries,
  relatedTickets = [],
  relatedFFCards = [],
  showConnectionTime = false,
  className,
}: FlightSegmentCardProps) {
  // Get location names from dictionaries
  const getLocationName = (code: string): string => {
    const location = dictionaries?.location?.[code];
    if (!location) return code;
    return location.airportName || location.cityName || code;
  };

  // Get meal description from dictionary or use code
  const getMealDescription = (code: string): string => {
    const mealDescriptions: Record<string, string> = {
      'B': 'Breakfast',
      'B5': 'Cold Breakfast',
      'K': 'Continental Breakfast',
      'L': 'Lunch',
      'D': 'Dinner',
      'S': 'Snack',
      'R': 'Refreshments',
      'M': 'Meal',
      'C': 'Alcoholic Beverages',
      'F': 'Food for Purchase',
      'G': 'Food/Beverages for Purchase',
      'H': 'Hot Meal',
      'N': 'No Meal',
      'O': 'Cold Meal',
      'P': 'Alcoholic Beverages for Purchase',
      'V': 'Refreshments for Purchase',
    };
    return dictionaries?.meal?.[code] || mealDescriptions[code] || code;
  };
  const statusColor = getStatusColor(segment.statusCode);
  const borderColorClass = statusColor === 'green'
    ? 'border-l-green-500'
    : statusColor === 'amber'
      ? 'border-l-amber-500'
      : 'border-l-slate-400';

  const date = formatDate(flightDetails.departure.dateTime);
  const depTime = formatTime(flightDetails.departure.dateTime);
  const arrTime = formatTime(flightDetails.arrival.dateTime);

  return (
    <div className={cn('space-y-2', className)}>
      <div
        className={cn(
          'relative rounded-lg bg-white border border-slate-200 p-3 md:p-4',
          'border-l-4 shadow-sm',
          'hover:shadow-md transition-all duration-200',
          borderColorClass
        )}
      >
        {/* Top row: Airline logo, flight number, route - responsive */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4">
          <div className="flex items-center gap-2 md:gap-3">
            {/* Smaller logo on mobile */}
            <AirlineLogo airlineCode={flightDetails.marketingAirlineCode} size="sm" className="md:hidden" />
            <AirlineLogo airlineCode={flightDetails.marketingAirlineCode} size="md" className="hidden md:block" />
            <div>
              <div className="text-lg md:text-xl font-bold text-slate-900">
                {flightDetails.marketingAirlineCode}{flightDetails.marketingFlightNumber}
              </div>
              <div className="flex items-center gap-1.5 text-xs md:text-sm text-slate-500">
                <span className="font-semibold text-slate-700" title={getLocationName(flightDetails.departure.locationCode)}>
                  {flightDetails.departure.locationCode}
                </span>
                <ArrowRight className="h-3 w-3" />
                <span className="font-semibold text-slate-700" title={getLocationName(flightDetails.arrival.locationCode)}>
                  {flightDetails.arrival.locationCode}
                </span>
                {dictionaries?.location && (
                  <span className="text-xs text-slate-400 ml-1 hidden md:inline">
                    ({getLocationName(flightDetails.departure.locationCode)} → {getLocationName(flightDetails.arrival.locationCode)})
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Date/time and class - row with wrapping on mobile */}
          <div className="flex flex-wrap items-center gap-2 md:gap-4 text-sm">
            <div className="flex items-center gap-1.5 text-slate-500">
              <Calendar className="h-3.5 w-3.5" />
              <span>{date}</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-600 font-medium">
              <Clock className="h-3.5 w-3.5" />
              <span>{depTime}</span>
              <span className="text-slate-400">→</span>
              <span>{arrTime}</span>
            </div>
            <BookingClassBadge bookingClass={segment.bookingClass} />
          </div>
        </div>

        {/* Bottom row: Status, duration, meals, tickets - wrap on mobile */}
        <div className="flex flex-wrap items-center gap-2 md:gap-4 mt-3 pt-3 border-t border-slate-100">
          <div className="flex flex-wrap items-center gap-2 md:gap-4">
            <BookingStatusBadge statusCode={segment.statusCode} />
            {flightDetails.duration && (
              <span className="text-xs text-slate-400">
                {formatDuration(flightDetails.duration)}
              </span>
            )}
            {segment.isFlown && (
              <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                Flown
              </span>
            )}
            {flightDetails.meals?.mealCodes && flightDetails.meals.mealCodes.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <Utensils className="h-3 w-3" />
                <span title={flightDetails.meals.mealCodes.map(getMealDescription).join(', ')}>
                  {flightDetails.meals.mealCodes.map(getMealDescription).join(', ')}
                </span>
              </div>
            )}
          </div>

          {/* Related tickets */}
          {relatedTickets.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">Tickets:</span>
              {relatedTickets.map(ticket => (
                <span
                  key={ticket.id}
                  className="text-xs font-mono text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded"
                >
                  {ticket.id.slice(-6)}
                </span>
              ))}
            </div>
          )}

          {/* Related FF cards */}
          {relatedFFCards.length > 0 && (
            <div className="flex items-center gap-1">
              {relatedFFCards.map(card => (
                <div
                  key={card.id}
                  className="flex items-center gap-1 bg-slate-50 px-1.5 py-0.5 rounded"
                >
                  <AirlineLogo airlineCode={card.companyCode} size="sm" />
                  <span className="text-xs font-mono text-slate-500">
                    {card.cardNumber.slice(-4)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Connection time indicator */}
      {showConnectionTime && segment.connectionTime && segment.connectionTime > 0 && (
        <div className="flex items-center justify-center py-2">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <div className="w-8 h-px bg-slate-200" />
            <Clock className="h-3 w-3" />
            <span>{formatDuration(segment.connectionTime)} connection</span>
            <div className="w-8 h-px bg-slate-200" />
          </div>
        </div>
      )}
    </div>
  );
}
